import { useCallback, useRef } from 'react';
import { useNewBlockAddedHandler } from './useNewBlockAddedHandler';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import { getHeightAndWidthFromDataUrl } from '../ImageLibrary/helpers';
import { useCanvasImageUploadHandler } from './useCanvasImageUploadHandler';
import { defaultBlockStyle } from './useBlockSettings';
import { useSelectionContext } from '../GridDndEditor/SelectedBlockInfoProvider';
import { GridBlockType } from 'services/entities/BlockTypes';
import { useEffectivePageSize } from 'hooks/useEffectivePageSize';
import useActiveSection from '../SectionsDrawer/useActiveSection';
import { useAppSelector } from '../grid/reduxStore/Store';
import { selectSections } from '../grid/reduxStore/editorSlice';
import type { Block } from '../GridDndEditor/models/Block.model';
import { getCenteredY, getGreaterZIndexAvailable, resolveTargetSectionId } from './sectionViewportUtils';

const CLICK_TO_ADD_IMAGE_WIDTH = 320;

export default function useClickToAddImage() {
  const { selectedSectionId, setSelectedBlockInfoByWrapper } = useSelectionContext();
  const { activeSection } = useActiveSection();
  const sections = useAppSelector(selectSections);
  const blockAddedHandler = useNewBlockAddedHandler();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();
  const { handleImageUrlUpload } = useCanvasImageUploadHandler();
  const canvasWidth = useEffectivePageSize().width;
  const canvasWidthRef = useRef(canvasWidth);
  const isAddingRef = useRef(false);
  canvasWidthRef.current = canvasWidth;

  return useCallback(
    async (rawSrc: string) => {
      if (isAddingRef.current) {
        return null;
      }

      isAddingRef.current = true;

      try {
        const src = await handleImageUrlUpload(rawSrc);
        if (!src) {
          return null;
        }

        const targetSectionId = resolveTargetSectionId(selectedSectionId, activeSection, sections);

        if (!targetSectionId) {
          return null;
        }

        const { width: imageWidth, height: imageHeight } = await getHeightAndWidthFromDataUrl(src);
        const calculatedHeight =
          imageWidth > 0 ? Math.round((CLICK_TO_ADD_IMAGE_WIDTH / imageWidth) * imageHeight) : CLICK_TO_ADD_IMAGE_WIDTH;

        const x = (canvasWidthRef.current - CLICK_TO_ADD_IMAGE_WIDTH) / 2;
        const blockConfig: Block = {
          x,
          y: getCenteredY(targetSectionId, calculatedHeight),
          z: getGreaterZIndexAvailable(targetSectionId),
          width: CLICK_TO_ADD_IMAGE_WIDTH,
          height: calculatedHeight,
        };

        const response = await blockAddedHandler(
          targetSectionId,
          { htmlContent: src, styles: defaultBlockStyle },
          blockConfig,
          GridBlockType.IMAGE
        );

        if (checkDocumentLockStatus(response.errorCode)) {
          return null;
        }

        const blockId = response.content.blockId;

        if (!blockId) {
          return null;
        }

        setSelectedBlockInfoByWrapper({ blockId, sectionId: targetSectionId });

        return blockId;
      } finally {
        isAddingRef.current = false;
      }
    },
    [
      activeSection,
      blockAddedHandler,
      checkDocumentLockStatus,
      handleImageUrlUpload,
      sections,
      selectedSectionId,
      setSelectedBlockInfoByWrapper,
    ]
  );
}
